/**
 * WorkbenchPluginHost — mounts a set of plugins over the workbench subtree.
 *
 * Two jobs: (1) fold each plugin's providers around `children` (outermost-first)
 * — exactly the by-hand `<LiveTrafficProvider>` wrap the Studio route does today,
 * generalized; (2) build the flattened contribution registry ONCE and provide it
 * via context. The registry value and its arrays are frozen and memoized on the
 * `plugins` reference, so the hot board render path reads a referentially-stable
 * list (no `= []` identity churn).
 *
 * Pass a STABLE `plugins` array (useMemo it at the mount site) — a fresh array
 * each render rebuilds the registry and remounts providers.
 */
import { Component, useMemo, type ComponentType, type ReactNode } from "react";
import { WORKBENCH_PLUGIN_API_VERSION, type WorkbenchPlugin } from "./types.js";
import {
  PluginRegistryProvider,
  type Owned,
  type PluginRegistry,
} from "./PluginRegistryContext.js";
import type {
  BoardLayerContribution,
  CommandContribution,
  DetailSectionContribution,
  HeaderItemContribution,
  StatusItemContribution,
  NavSectionContribution,
  PanelKind,
  MenuItemContribution,
  ContextMenuContribution,
  SettingsPageContribution,
} from "./types.js";

/** A plugin is compatible when it omits apiVersion (first-party, pinned) or
 *  declares exactly the host version. Others are dropped with a warning. */
export function isPluginCompatible(p: WorkbenchPlugin): boolean {
  return p.apiVersion === undefined || p.apiVersion === WORKBENCH_PLUGIN_API_VERSION;
}

/** Filter to the compatible plugins, warning (once per incompatible) so an
 *  outdated integration degrades to absent instead of crashing the workbench. */
export function compatiblePlugins(plugins: readonly WorkbenchPlugin[]): readonly WorkbenchPlugin[] {
  return plugins.filter((p) => {
    if (isPluginCompatible(p)) return true;
    // eslint-disable-next-line no-console
    console.warn(
      `[workbench-plugin ${p.id}] targets API v${p.apiVersion}, host is ` +
        `v${WORKBENCH_PLUGIN_API_VERSION} — skipping.`,
    );
    return false;
  });
}

function buildRegistry(plugins: readonly WorkbenchPlugin[]): PluginRegistry {
  const boardLayers: Owned<BoardLayerContribution>[] = [];
  const detailSections: Owned<DetailSectionContribution>[] = [];
  const headerItems: Owned<HeaderItemContribution>[] = [];
  const statusItems: Owned<StatusItemContribution>[] = [];
  const navSections: Owned<NavSectionContribution>[] = [];
  const panelKinds = new Map<string, Owned<PanelKind>>();
  const commands: Owned<CommandContribution>[] = [];
  const menuItems: Owned<MenuItemContribution>[] = [];
  const contextMenu: Owned<ContextMenuContribution>[] = [];
  const settingsPages: Owned<SettingsPageContribution>[] = [];
  for (const p of plugins) {
    const c = p.contributes;
    if (!c) continue;
    for (const l of c.boardLayers ?? []) boardLayers.push({ pluginId: p.id, contribution: l });
    for (const s of c.detailSections ?? []) detailSections.push({ pluginId: p.id, contribution: s });
    for (const h of c.headerItems ?? []) headerItems.push({ pluginId: p.id, contribution: h });
    for (const s of c.statusItems ?? []) statusItems.push({ pluginId: p.id, contribution: s });
    for (const n of c.navSections ?? []) navSections.push({ pluginId: p.id, contribution: n });
    for (const m of c.menuItems ?? []) menuItems.push({ pluginId: p.id, contribution: m });
    for (const cm of c.contextMenu ?? []) contextMenu.push({ pluginId: p.id, contribution: cm });
    for (const sp of c.settingsPages ?? []) settingsPages.push({ pluginId: p.id, contribution: sp });
    for (const k of c.panelKinds ?? []) {
      // First registration of a kind wins; a later duplicate is dropped so two
      // plugins can't fight over one tab kind (mirrors compatiblePlugins).
      const prev = panelKinds.get(k.kind);
      if (prev) {
        // eslint-disable-next-line no-console
        console.warn(
          `[workbench-plugin ${p.id}] panelKind "${k.kind}" already registered ` +
            `by "${prev.pluginId}" — skipping.`,
        );
        continue;
      }
      panelKinds.set(k.kind, { pluginId: p.id, contribution: k });
    }
    for (const cmd of c.commands ?? []) commands.push({ pluginId: p.id, contribution: cmd });
  }
  navSections.sort(
    (a, b) => (a.contribution.order ?? 0) - (b.contribution.order ?? 0),
  );
  menuItems.sort((a, b) => (a.contribution.order ?? 0) - (b.contribution.order ?? 0));
  settingsPages.sort((a, b) => (a.contribution.order ?? 0) - (b.contribution.order ?? 0));
  return Object.freeze({
    boardLayers: Object.freeze(boardLayers),
    detailSections: Object.freeze(detailSections),
    headerItems: Object.freeze(headerItems),
    statusItems: Object.freeze(statusItems),
    navSections: Object.freeze(navSections),
    panelKinds,
    commands: Object.freeze(commands),
    menuItems: Object.freeze(menuItems),
    contextMenu: Object.freeze(contextMenu),
    settingsPages: Object.freeze(settingsPages),
  });
}

export function WorkbenchPluginHost({
  plugins,
  children,
}: {
  readonly plugins: readonly WorkbenchPlugin[];
  readonly children: ReactNode;
}): ReactNode {
  // Drop API-incompatible plugins up front so neither their contributions nor
  // their providers are mounted.
  const active = useMemo(() => compatiblePlugins(plugins), [plugins]);
  const registry = useMemo(() => buildRegistry(active), [active]);
  const providers = useMemo<readonly ComponentType<{ children: ReactNode }>[]>(
    () => active.flatMap((p) => p.providers ?? []),
    [active],
  );

  let tree: ReactNode = (
    <PluginRegistryProvider value={registry}>{children}</PluginRegistryProvider>
  );
  // Wrap from the inside out so providers[0] ends up outermost.
  for (let i = providers.length - 1; i >= 0; i--) {
    const Provider = providers[i]!;
    tree = <Provider>{tree}</Provider>;
  }
  return tree;
}

/**
 * Renders its children, or nothing if they throw — so one plugin's crashing
 * contribution degrades to "absent" rather than taking down the board or the
 * details panel. Consumers wrap each contributed Layer/Section in one, keyed by
 * plugin id.
 */
export class PluginBoundary extends Component<
  { readonly pluginId: string; readonly children: ReactNode },
  { readonly failed: boolean }
> {
  override state = { failed: false };

  static getDerivedStateFromError(): { failed: boolean } {
    return { failed: true };
  }

  override componentDidCatch(error: unknown): void {
    // Surface it for debugging; the UI just drops this contribution.
    // eslint-disable-next-line no-console
    console.error(`[workbench-plugin ${this.props.pluginId}] contribution crashed:`, error);
  }

  override render(): ReactNode {
    return this.state.failed ? null : this.props.children;
  }
}
